import React, { lazy, Suspense, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import axios from 'axios';

import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";
import BackgroundMusic from "./components/common/BackgroundMusic";

// Lazy load components for performance (code-split by route)
const LandingPage = lazy(() => import(/* webpackChunkName: "landing" */ "./components/landing/LandingPage"));
const ContactPage = lazy(() => import(/* webpackChunkName: "contact" */ "./components/landing/ContactPage"));
const DomeHouse = lazy(() => import(/* webpackChunkName: "dome-house" */ "./components/landing/Models/DomeHouse"));
const SleepingPod = lazy(() => import(/* webpackChunkName: "sleeping-pod" */ "./components/landing/Models/SleepingPod"));
const V007Cabin = lazy(() => import(/* webpackChunkName: "v007" */ "./components/landing/Models/V007Cabin"));
const V009Cabin = lazy(() => import(/* webpackChunkName: "v009" */ "./components/landing/Models/V009Cabin"));
const V011Cabin = lazy(() => import(/* webpackChunkName: "v011" */ "./components/landing/Models/V011Cabin"));
const V012Cabin = lazy(() => import(/* webpackChunkName: "v012" */ "./components/landing/Models/V012Cabin"));
const CCabin = lazy(() => import(/* webpackChunkName: "c-cabin" */ "./components/landing/Models/CCabin"));
const V008Cabin = lazy(() => import(/* webpackChunkName: "v008" */ "./components/landing/Models/V008Cabin"));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy" */ "./components/landing/PrivacyPolicy"));
const TermsOfUse = lazy(() => import(/* webpackChunkName: "terms" */ "./components/landing/TermsOfUse"));
const ConceptPage = lazy(() => import(/* webpackChunkName: "concept" */ "./components/landing/ConceptPage"));
const NotFoundPage = lazy(() => import(/* webpackChunkName: "404" */ "./components/landing/NotFoundPage.jsx"));

// Minimal, performant loading fallback (matches the inline skeleton in index.html)
const LoadingFallback = () => (
  <div className="h-screen w-full flex items-center justify-center bg-black">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      <span className="text-amber-500 font-premium-serif tracking-widest uppercase text-xs animate-pulse">VAIGA</span>
    </div>
  </div>
);



/**
 * RoutePreloader - Prefetches adjacent route chunks after the main page loads.
 * This ensures navigating to model pages feels instant (no loading spinner).
 */
const RoutePreloader = () => {
  const location = useLocation();

  useEffect(() => {
    // After initial paint, prefetch commonly navigated routes
    const timer = setTimeout(() => {
      if (location.pathname === '/') {
        // On landing page, prefetch contact and popular models
        import(/* webpackChunkName: "contact" */ "./components/landing/ContactPage");
        import(/* webpackChunkName: "v007" */ "./components/landing/Models/V007Cabin");
      }
    }, 3000); // Wait 3s after page load to not compete with critical resources

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <RoutePreloader />
        <BackgroundMusic />
        <div className="App">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/dome-house" element={<DomeHouse />} />
              <Route path="/sleeping-pod" element={<SleepingPod />} />
              <Route path="/v007-cabin" element={<V007Cabin />} />
              <Route path="/v009-cabin" element={<V009Cabin />} />
              <Route path="/v011-cabin" element={<V011Cabin />} />
              <Route path="/v012-cabin" element={<V012Cabin />} />
              <Route path="/c-cabin" element={<CCabin />} />
              <Route path="/v008-cabin" element={<V008Cabin />} />
              <Route path="/concept" element={<ConceptPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <Toaster />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
